import React, { useState, useEffect } from 'react';
import { 
  Modal, Form, Button, Alert, InputGroup, ListGroup, 
  Badge, Card, Row, Col, Spinner
} from 'react-bootstrap';
import { Plus, Minus, Calendar, DollarSign, Trash2 } from 'lucide-react';
import expenseService from '../../services/expenseService';
import { cashSessionService } from '../../services/Cashsessionservice';
import { useAuth } from '../../contexts/AuthContext';
import { useBoutique } from '../../contexts/BoutiqueContext';

const ExpenseManager = ({ 
  show, 
  onHide, 
  onExpenseAdded, 
  onExpenseDeleted, // ✅ NOUVELLE PROP
  currentDate = new Date(),
  sessionId = null // ✅ Si fourni : dépenses de la session uniquement
}) => {
  const { user } = useAuth();
  const isManager = user?.role === "MANAGER";
  const {currentBoutique } = useBoutique();

  // États
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // États du formulaire
  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    category: 'GENERAL',
    date: new Date().toISOString().slice(0, 16)
  });

  // Charger les dépenses du jour
  const loadTodayExpenses = async () => {
    try {
      setLoading(true);
      let list;
      if (sessionId) {
        const resp = await cashSessionService.getSessionExpenses(sessionId);
        list = resp.success && Array.isArray(resp.data) ? resp.data : [];
      } else {
        list = await expenseService.getExpensesByDate(currentDate);
      }
      setExpenses(list);
    } catch (error) {
      console.error('❌ Erreur lors du chargement des dépenses:', error);
      setError('Erreur lors du chargement des dépenses');
    } finally {
      setLoading(false);
    }
  };

  // Ajouter une dépense
  const handleAddExpense = async (e) => {
    e.preventDefault();
    const validation = expenseService.validateExpense(formData);
          if (!validation.isValid) {
            alert(validation.errors.join('\n'));
            return;
          }


    
    if (!formData.amount || !formData.description) {
      setError('Montant et description sont obligatoires');
      return;
    }

    if (parseFloat(formData.amount) <= 0) {
      setError('Le montant doit être supérieur à 0');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const expenseData = {
        ...formData,
        amount: parseFloat(formData.amount),
        boutiqueId: currentBoutique?.id,
        createdBy: user?.id
      };

      const response = await expenseService.addExpense(expenseData);
      
      if (response.success) {
        setSuccess('Dépense ajoutée avec succès');
        
        // Réinitialiser le formulaire
        setFormData({
          amount: '',
          description: '',
          category: 'GENERAL',
          date: new Date().toISOString().slice(0, 16)
        });
        
        // Recharger les dépenses
        await loadTodayExpenses();
        
        // Notifier le parent pour mettre à jour le solde de caisse
        if (onExpenseAdded) {
          onExpenseAdded(response.data);
        }

        // Effacer le message de succès après 3 secondes
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.message || 'Erreur lors de l\'ajout de la dépense');
      }
    } catch (error) {
      console.error('❌ Erreur:', error);
      setError('Erreur lors de l\'ajout de la dépense');
    } finally {
      setLoading(false);
    }
  };

  // Supprimer une dépense
  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette dépense ?')) {
      return;
    }

    try {
      const response = await expenseService.deleteExpense(expenseId);
      if (response.success) {
        setSuccess('Dépense supprimée avec succès');
        await loadTodayExpenses();
      
        // ✅ Appeler la nouvelle prop
        if (onExpenseDeleted) {
          onExpenseDeleted();
        }
        // Réinitialiser le formulaire
        setFormData({
          amount: '',
          description: '',
          category: 'GENERAL',
          date: new Date().toISOString().slice(0, 16)
        });
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      setError('Erreur lors de la suppression de la dépense');
    }
  };

  // Calculer le total des dépenses
  const totalExpenses = expenses.reduce((sum, expense) => sum + expense.amount, 0);

  // Charger les dépenses au montage
  useEffect(() => {
    if (show) {
      loadTodayExpenses();
      // Réinitialiser les messages
      setError(null);
      setSuccess(null);
    }
  }, [show, currentDate, sessionId]);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title>
          <Minus className="me-2 text-danger" size={20} />
          Gestion des dépenses
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {/* Résumé des dépenses du jour */}
        <Card className="mb-4 border-danger border-2">
          <Card.Body className="text-center py-3">
            <div className="d-flex align-items-center justify-content-center">
              <div className="bg-danger bg-opacity-10 rounded-circle p-3 me-3">
                <Minus size={24} className="text-danger" />
              </div>
              <div>
                <small className="text-muted">Total dépenses du jour</small>
                <div className="h4 text-danger mb-0">
                  {expenseService.formatCurrency(totalExpenses)}
                </div>
                <small className="text-muted">
                  {expenses.length} dépense{expenses.length > 1 ? 's' : ''}
                </small>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Messages d'alerte */}
        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
        {success && <Alert variant="success" className="mb-3">{success}</Alert>}

        {/* Formulaire d'ajout */}
        <Card className="mb-4">
          <Card.Header className="bg-primary text-white">
            <h6 className="mb-0">
              <Plus size={16} className="me-2" />
              Ajouter une nouvelle dépense
            </h6>
          </Card.Header>
          <Card.Body>
            <Form onSubmit={handleAddExpense}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Montant *</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        onWheel={e => e.target.blur()} // Empêche le scroll de changer la valeur
                        placeholder="0"
                        value={formData.amount}
                        onChange={(e) => setFormData({...formData, amount: e.target.value})}
                        min="0"
                        step="0.01"
                        required
                      />
                      <InputGroup.Text>XOF</InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Catégorie</Form.Label>
                    <Form.Select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                    >
                      {expenseService.getExpenseCategories().map(cat => (
                        <option key={cat.value} value={cat.value}>
                          {cat.icon} {cat.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Description *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Description de la dépense..."
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Date et heure</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                />
              </Form.Group>

              

              <Button 
                type="submit" 
                variant="danger" 
                disabled={loading}
                className="w-100"
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Ajout en cours...
                  </>
                ) : (
                  <>
                    <Plus size={16} className="me-2" />
                    Ajouter la dépense
                  </>
                )}
              </Button>
            </Form>
          </Card.Body>
        </Card>

        {/* Liste des dépenses du jour */}
        {loading && expenses.length === 0 ? (
          <div className="text-center py-3">
            <Spinner /> <span className="ms-2">Chargement...</span>
          </div>
        ) : expenses.length > 0 ? (
          <Card>
            <Card.Header>
              <h6 className="mb-0">Dépenses d'aujourd'hui</h6>
            </Card.Header>
            <ListGroup variant="flush">
              {expenses.map((expense, index) => {
                const category = expenseService.getExpenseCategories()
                  .find(cat => cat.value === expense.category);
                
                return (
                  <ListGroup.Item key={expense.id || index} className="d-flex justify-content-between align-items-center">
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center mb-1">
                        <span className="me-2">{category?.icon || '📝'}</span>
                        <strong>{expense.description}</strong>
                      </div>
                      <small className="text-muted">
                        {category?.label || 'Général'} • {new Date(expense.date).toLocaleString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </small>
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center">
                      <Badge bg="danger" className="fs-6 me-2">
                        -{expenseService.formatCurrency(expense.amount)}
                      </Badge>
                      
                    </div>
                    {isManager && (
                      <small className="text-muted">
                        Par {expense.createdByName || 'Inconnu'}
                      </small>
                    )}  
                    </div>
                    
                    
                    {isManager && (

                    <div className="d-flex align-items-center">
                      

                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteExpense(expense.id)}
                        title="Supprimer cette dépense"
                      >
                        <Trash2 size={14} />
                      </Button>
                      
                    </div>
                    )}

                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          </Card>
        ) : (
          <div className="text-center py-4 text-muted">
            <Minus size={48} className="mb-2 opacity-25" />
            <p>Aucune dépense enregistrée aujourd'hui</p>
          </div>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fermer
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExpenseManager;